import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Spinner } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import { ordenarPorTallaYColor } from "../../../../utils/tallas";

// Verificar la entrada de un maquilero escaneando el QR de cada prenda (el mismo QR de las
// etiquetas: "CODIGO$COLOR$TALLA"). Compara contra lo pendiente de recibir de la salida:
//  - tablero talla × color que se va llenando, con sonido distinto si entra bien, sobra o no es
//    de esta salida;
//  - el avance se guarda en el navegador (si se cierra o se va la luz no se pierde);
//  - "Pasar a la entrada" abre la captura de entrada con las cantidades escaneadas.
const URL_ARTICULOS_PROCESOS_SALIDA = process.env.REACT_APP_URL_ARTICULOS_PROCESOS_SALIDA;

// Igual que qrPayload: mayúsculas, sin acentos, espacios normalizados
const norm = (v) =>
  String(v == null ? "" : v)
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/\s+/g, " ")
    .trim();

const claveCelda = (talla, color) => `${norm(talla)}|${norm(color)}`;
const claveStorage = (id) => `verificarEntradaQR:${id}`;

// Sonidos cortos con WebAudio (sin archivos): ok = agudo, sobra = doble, error = grave
let audioCtx = null;
function sonar(tipo) {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    audioCtx = audioCtx || new AC();
    const notas = {
      ok: [[1046, 0.08]],
      sobra: [[660, 0.09], [660, 0.09]],
      error: [[196, 0.28]],
      fin: [[523, 0.12], [659, 0.12], [784, 0.12], [1046, 0.3]],
    }[tipo] || [];
    let t = audioCtx.currentTime;
    notas.forEach(([f, d]) => {
      const o = audioCtx.createOscillator();
      const g = audioCtx.createGain();
      o.type = tipo === "error" ? "sawtooth" : "sine";
      o.frequency.value = f;
      g.gain.setValueAtTime(0.18, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + d);
      o.connect(g);
      g.connect(audioCtx.destination);
      o.start(t);
      o.stop(t + d);
      t += d + 0.04;
    });
  } catch (e) {
    // sin audio: solo se ve el aviso en pantalla
  }
}

const COLORES_AVISO = {
  ok: { bg: "#16a34a", fg: "#fff" },
  sobra: { bg: "#f59e0b", fg: "#1f2937" },
  error: { bg: "#dc2626", fg: "#fff" },
};

function VerificarEntradaQR({ isOpen, toggle, salida, onPasarEntrada }) {
  const [cargando, setCargando] = useState(false);
  const [lineas, setLineas] = useState([]); // [{id, talla, color, pendiente}]
  const [conteo, setConteo] = useState({}); // claveCelda -> escaneadas
  const [historial, setHistorial] = useState([]); // [{clave, tipo, texto, hora}]
  const [rechazos, setRechazos] = useState([]); // [{texto, motivo, hora}]
  const [aviso, setAviso] = useState(null); // {tipo, titulo, detalle}
  const [ultimaCelda, setUltimaCelda] = useState("");
  const [scan, setScan] = useState("");
  const scanRef = useRef(null);
  const yaCelebro = useRef(false);

  const codigoSalida = salida ? norm((salida.articulo || "").split("/")[0]) : "";
  // Prenda de varias piezas (conjunto de 2 = 2): cada pieza trae su etiqueta, así que se
  // esperan pendiente × piezas escaneos y a la entrada pasan solo las prendas completas.
  const piezas = Math.max(1, parseInt(salida && salida.piezasPrenda, 10) || 1);
  const enfocar = () => setTimeout(() => scanRef.current && scanRef.current.focus(), 0);

  // Carga lo pendiente de recibir y recupera el avance guardado
  useEffect(() => {
    if (!isOpen || !salida) return;
    setCargando(true);
    setAviso(null);
    setScan("");
    yaCelebro.current = false;
    let guardado = {};
    try {
      guardado = JSON.parse(localStorage.getItem(claveStorage(salida.id)) || "{}");
    } catch (e) {
      guardado = {};
    }
    setConteo(guardado.conteo || {});
    setHistorial(guardado.historial || []);
    setRechazos(guardado.rechazos || []);
    axios
      .get(`${URL_ARTICULOS_PROCESOS_SALIDA}/proceso/${salida.id}`, {
        headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
      })
      .then((r) => {
        const ls = (r.data || []).map((l) => {
          const ap = (l.articulosPedido || [])[0] || {};
          const ac = (l.articulosCortes || [])[0] || {};
          const color = ((ap.colores || l.colores || ac.colores || [])[0] || {}).name || "";
          const talla = ((ap.tallas || l.tallas || ac.tallas || [])[0] || {}).name || "";
          return { id: l._id, talla, color, pendiente: Math.max(0, parseFloat(l.faltantes) || 0) };
        });
        setLineas(ls);
      })
      .catch(() => {
        setLineas([]);
        Swal.fire({ icon: "error", title: "No se pudieron cargar las piezas de la salida" });
      })
      .finally(() => {
        setCargando(false);
        enfocar();
      });
  }, [isOpen, salida && salida.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Guarda el avance en cada escaneo
  useEffect(() => {
    if (!isOpen || !salida) return;
    try {
      localStorage.setItem(
        claveStorage(salida.id),
        JSON.stringify({ conteo, historial: historial.slice(0, 200), rechazos: rechazos.slice(0, 100) })
      );
    } catch (e) {
      // almacenamiento lleno o bloqueado: se sigue sin guardar
    }
  }, [conteo, historial, rechazos]); // eslint-disable-line react-hooks/exhaustive-deps

  // Celdas talla × color (talla primero) con lo esperado
  const celdas = useMemo(() => {
    const m = {};
    lineas.forEach((l) => {
      const k = claveCelda(l.talla, l.color);
      m[k] = m[k] || { clave: k, talla: l.talla, color: l.color, esperado: 0, lineas: [] };
      m[k].esperado += l.pendiente * piezas;
      m[k].lineas.push(l);
    });
    return m;
  }, [lineas, piezas]);

  const tallas = useMemo(
    () => [...new Set(ordenarPorTallaYColor(Object.values(celdas), (c) => c.talla, (c) => c.color).map((c) => c.talla))],
    [celdas]
  );
  const colores = useMemo(
    () => [...new Set(Object.values(celdas).map((c) => c.color))].sort((a, b) => a.localeCompare(b)),
    [celdas]
  );

  const esperadas = Object.values(celdas).reduce((t, c) => t + c.esperado, 0);
  let validas = 0;
  let sobrantes = 0;
  Object.values(celdas).forEach((c) => {
    const n = conteo[c.clave] || 0;
    validas += Math.min(n, c.esperado);
    sobrantes += Math.max(0, n - c.esperado);
  });
  const faltan = Math.max(0, esperadas - validas);
  const pct = esperadas ? Math.round((validas / esperadas) * 100) : 0;
  const completa = esperadas > 0 && faltan === 0;
  // Prendas completas por celda (todas sus piezas escaneadas)
  const prendasDe = (c) => Math.floor(Math.min(conteo[c.clave] || 0, c.esperado) / piezas);
  const prendas = Object.values(celdas).reduce((t, c) => t + prendasDe(c), 0);
  const piezasSueltas = validas - prendas * piezas;

  useEffect(() => {
    if (completa && !yaCelebro.current && historial.length) {
      yaCelebro.current = true;
      setTimeout(() => sonar("fin"), 250);
    }
    if (!completa) yaCelebro.current = false;
  }, [completa]); // eslint-disable-line react-hooks/exhaustive-deps

  const hora = () => new Date().toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

  function rechazar(texto, motivo) {
    sonar("error");
    setAviso({ tipo: "error", titulo: motivo, detalle: `Se leyó: ${texto}` });
    setRechazos((prev) => [{ texto, motivo, hora: hora() }, ...prev]);
  }

  function procesar(texto) {
    const t = (texto || "").trim();
    setScan("");
    if (!t) return;
    // Mismos separadores que VentaDirecta: "$" (nuevo) y "|" (viejo, llega como "]", "}" o "¦")
    const partes = t.split(/[$|\]}¦]+/);
    const codigo = norm(partes[0]);
    const color = partes[1] || "";
    const talla = partes[2] || "";
    if (!color || !talla) return rechazar(t, "No parece una etiqueta de prenda");
    if (codigo !== codigoSalida) return rechazar(t, `Es otro modelo (${codigo}), no ${codigoSalida}`);
    const k = claveCelda(talla, color);
    const c = celdas[k];
    if (!c) return rechazar(t, `${norm(talla)} / ${norm(color)} no salió en esta maquila`);

    const n = (conteo[k] || 0) + 1;
    setConteo((prev) => ({ ...prev, [k]: (prev[k] || 0) + 1 }));
    const sobra = n > c.esperado;
    sonar(sobra ? "sobra" : "ok");
    setUltimaCelda(k);
    setAviso({
      tipo: sobra ? "sobra" : "ok",
      titulo: sobra ? `SOBRA: ${c.talla} / ${c.color}` : `+1  ${c.talla} / ${c.color}`,
      detalle: sobra
        ? `Ya estaban las ${c.esperado} pendientes; esta pieza es de más`
        : `${n} de ${c.esperado}${n === c.esperado ? " — ¡completo!" : ""}`,
    });
    setHistorial((prev) => [{ clave: k, tipo: sobra ? "sobra" : "ok", texto: `${c.talla} / ${c.color}`, hora: hora() }, ...prev]);
  }

  function quitarUltima() {
    const [ultima, ...resto] = historial;
    if (!ultima) return;
    setConteo((prev) => ({ ...prev, [ultima.clave]: Math.max(0, (prev[ultima.clave] || 0) - 1) }));
    setHistorial(resto);
    setAviso({ tipo: "sobra", titulo: `Se quitó: ${ultima.texto}`, detalle: "" });
    enfocar();
  }

  async function reiniciar() {
    const r = await Swal.fire({
      icon: "warning",
      title: "¿Borrar todo lo escaneado?",
      showCancelButton: true,
      confirmButtonText: "Sí, empezar de nuevo",
      cancelButtonText: "No",
    });
    if (!r.value) return enfocar();
    setConteo({});
    setHistorial([]);
    setRechazos([]);
    setAviso(null);
    setUltimaCelda("");
    enfocar();
  }

  async function pasarEntrada() {
    if (!prendas) {
      Swal.fire({
        icon: "warning",
        title: validas ? "Ninguna prenda está completa" : "Todavía no hay piezas escaneadas",
        text: validas ? `Cada prenda lleva ${piezas} piezas; escanea todas sus etiquetas.` : "",
      });
      return enfocar();
    }
    const avisos = [];
    if (faltan) avisos.push(`Faltan <b>${faltan}</b> etiqueta(s) por escanear; se capturan solo las escaneadas.`);
    if (piezasSueltas) avisos.push(`<b>${piezasSueltas}</b> pieza(s) sin completar su prenda (de ${piezas}); no pasan.`);
    if (sobrantes) avisos.push(`Hay <b>${sobrantes}</b> pieza(s) de más; no pasan a la entrada.`);
    if (avisos.length) {
      const r = await Swal.fire({
        icon: "question",
        title: "¿Pasar a la entrada?",
        html: avisos.join("<br/>"),
        showCancelButton: true,
        confirmButtonText: "Sí, pasar",
        cancelButtonText: "Seguir escaneando",
      });
      if (!r.value) return enfocar();
    }
    // Reparte lo escaneado de cada celda entre sus renglones, hasta lo pendiente de cada uno
    const porLinea = {};
    Object.values(celdas).forEach((c) => {
      let resta = prendasDe(c);
      c.lineas.forEach((l) => {
        const v = Math.min(resta, l.pendiente);
        porLinea[l.id] = v;
        resta -= v;
      });
    });
    // El avance NO se borra aquí: si cancelan la captura o falla el guardado se conserva.
    // ListadoCortes lo borra cuando la entrada se guarda bien.
    onPasarEntrada(porLinea);
  }

  if (!salida) return null;
  const colorAviso = aviso ? COLORES_AVISO[aviso.tipo] : null;

  return (
    <Modal size="xl" isOpen={isOpen} toggle={toggle} onOpened={enfocar}>
      <ModalHeader toggle={toggle}>
        <i className="fas fa-qrcode" /> Verificar entrada — Salida #{salida.numero}
        <div style={{ fontSize: 14, fontWeight: 400, marginTop: 4 }}>
          {salida.proveedoresName} · {salida.articulo} · Corte {salida.numeroCorte}
        </div>
        {piezas > 1 ? (
          <div style={{ fontSize: 13, fontWeight: 600, color: "#b45309", marginTop: 2 }}>
            Prenda de {piezas} piezas: escanea la etiqueta de cada pieza (se esperan {piezas} por prenda)
          </div>
        ) : null}
      </ModalHeader>
      <ModalBody onClick={(e) => e.target.tagName !== "INPUT" && e.target.tagName !== "BUTTON" && enfocar()}>
        {cargando ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spinner />
          </div>
        ) : !esperadas ? (
          <h5 style={{ textAlign: "center", padding: 30 }}>Esta salida ya no tiene piezas pendientes de recibir.</h5>
        ) : (
          <>
            <style>{`
              @keyframes verifPulso { 0% { transform: scale(1.12); box-shadow: 0 0 0 4px rgba(22,163,74,.45); } 100% { transform: scale(1); box-shadow: none; } }
              @keyframes verifEntra { from { opacity: .3; transform: translateY(-6px); } to { opacity: 1; transform: none; } }
            `}</style>

            {/* Escáner + aviso del último escaneo */}
            <div style={{ display: "flex", gap: 16, alignItems: "stretch", flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 280px" }}>
                <Input
                  innerRef={scanRef}
                  value={scan}
                  placeholder="Escanea el QR de cada prenda…"
                  autoComplete="off"
                  style={{ fontSize: 20, height: 52, borderWidth: 2 }}
                  onChange={(e) => setScan(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      procesar(e.target.value);
                    }
                  }}
                />
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                  El avance se guarda solo; puedes cerrar y seguir después.
                </div>
              </div>
              <div
                key={historial.length + rechazos.length}
                style={{
                  flex: "1 1 280px",
                  borderRadius: 12,
                  padding: "8px 16px",
                  minHeight: 52,
                  background: colorAviso ? colorAviso.bg : "#f3f4f6",
                  color: colorAviso ? colorAviso.fg : "#6b7280",
                  animation: "verifEntra .25s ease-out",
                }}
              >
                {aviso ? (
                  <>
                    <div style={{ fontSize: 20, fontWeight: 700 }}>{aviso.titulo}</div>
                    {aviso.detalle ? <div style={{ fontSize: 14 }}>{aviso.detalle}</div> : null}
                  </>
                ) : (
                  <div style={{ fontSize: 16, paddingTop: 6 }}>Listo para escanear</div>
                )}
              </div>
            </div>

            {/* Avance general */}
            <div style={{ display: "flex", gap: 12, margin: "18px 0 8px", flexWrap: "wrap" }}>
              {[
                ["Escaneadas", validas, "#16a34a"],
                ["Faltan", faltan, faltan ? "#dc2626" : "#16a34a"],
                ["De más", sobrantes, sobrantes ? "#f59e0b" : "#9ca3af"],
                ["No son de aquí", rechazos.length, rechazos.length ? "#dc2626" : "#9ca3af"],
              ].map(([t, v, c]) => (
                <div key={t} style={{ flex: "1 1 120px", border: "1px solid #e5e7eb", borderRadius: 12, padding: "8px 12px" }}>
                  <div style={{ fontSize: 12, color: "#6b7280", textTransform: "uppercase" }}>{t}</div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: c }}>{v}</div>
                </div>
              ))}
            </div>
            <div style={{ background: "#e5e7eb", borderRadius: 999, height: 18, overflow: "hidden" }}>
              <div
                style={{
                  width: `${pct}%`,
                  height: "100%",
                  background: completa ? "#16a34a" : "linear-gradient(90deg,#2563eb,#16a34a)",
                  transition: "width .3s",
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 700,
                  textAlign: "right",
                  paddingRight: 8,
                }}
              >
                {pct}%
              </div>
            </div>
            {completa ? (
              <div
                style={{
                  marginTop: 12,
                  padding: 12,
                  borderRadius: 12,
                  background: "#dcfce7",
                  color: "#166534",
                  fontSize: 20,
                  fontWeight: 700,
                  textAlign: "center",
                  animation: "verifEntra .4s ease-out",
                }}
              >
                🎉 ¡Llegó completo! {validas} de {esperadas} piezas{sobrantes ? ` (y ${sobrantes} de más)` : ""}
              </div>
            ) : null}

            {/* Tablero talla × color */}
            <div style={{ overflowX: "auto", marginTop: 16 }}>
              <table style={{ borderCollapse: "separate", borderSpacing: 6, width: "100%" }}>
                <thead>
                  <tr>
                    <th style={{ fontSize: 12, color: "#6b7280" }}>TALLA</th>
                    {colores.map((c) => (
                      <th key={c} style={{ fontSize: 12, color: "#6b7280", textAlign: "center" }}>{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tallas.map((t) => (
                    <tr key={t}>
                      <td style={{ fontWeight: 700, fontSize: 18 }}>{t}</td>
                      {colores.map((col) => {
                        const k = claveCelda(t, col);
                        const c = celdas[k];
                        if (!c) return <td key={col} />;
                        const n = conteo[k] || 0;
                        const lleno = c.esperado ? Math.min(100, (n / c.esperado) * 100) : 100;
                        const estado = n > c.esperado ? "sobra" : n === c.esperado ? "ok" : "pend";
                        const borde = { sobra: "#f59e0b", ok: "#16a34a", pend: "#d1d5db" }[estado];
                        return (
                          <td key={col} style={{ padding: 0 }}>
                            <div
                              key={k === ultimaCelda ? `${k}-${historial.length}` : k}
                              style={{
                                position: "relative",
                                border: `2px solid ${borde}`,
                                borderRadius: 10,
                                padding: "6px 8px",
                                minWidth: 86,
                                textAlign: "center",
                                overflow: "hidden",
                                animation: k === ultimaCelda ? "verifPulso .5s ease-out" : undefined,
                              }}
                            >
                              <div
                                style={{
                                  position: "absolute",
                                  left: 0,
                                  bottom: 0,
                                  width: "100%",
                                  height: `${lleno}%`,
                                  background: estado === "sobra" ? "rgba(245,158,11,.25)" : "rgba(22,163,74,.18)",
                                  transition: "height .3s",
                                }}
                              />
                              <div style={{ position: "relative", fontSize: 18, fontWeight: 700 }}>
                                {n}
                                <span style={{ fontSize: 13, fontWeight: 400, color: "#6b7280" }}> / {c.esperado}</span>
                                {estado === "ok" ? <span style={{ color: "#16a34a" }}> ✓</span> : null}
                                {estado === "sobra" ? <span style={{ color: "#b45309" }}> +{n - c.esperado}</span> : null}
                              </div>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Últimos escaneos y rechazos */}
            <div style={{ display: "flex", gap: 16, marginTop: 12, flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 280px" }}>
                <div style={{ fontSize: 12, color: "#6b7280", textTransform: "uppercase", marginBottom: 4 }}>Últimos escaneos</div>
                {historial.slice(0, 6).map((h, i) => (
                  <div key={`${h.hora}-${i}`} style={{ fontSize: 14, color: h.tipo === "sobra" ? "#b45309" : "#166534" }}>
                    {h.hora} · {h.texto} {h.tipo === "sobra" ? "(de más)" : ""}
                  </div>
                ))}
                {!historial.length ? <div style={{ fontSize: 14, color: "#9ca3af" }}>—</div> : null}
              </div>
              <div style={{ flex: "1 1 280px" }}>
                <div style={{ fontSize: 12, color: "#6b7280", textTransform: "uppercase", marginBottom: 4 }}>No son de esta salida</div>
                {rechazos.slice(0, 6).map((r, i) => (
                  <div key={`${r.hora}-${i}`} style={{ fontSize: 14, color: "#b91c1c" }}>
                    {r.hora} · {r.motivo}
                  </div>
                ))}
                {!rechazos.length ? <div style={{ fontSize: 14, color: "#9ca3af" }}>—</div> : null}
              </div>
            </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={quitarUltima} disabled={!historial.length}>
          <i className="fas fa-undo" /> Quitar última
        </Button>
        <Button color="danger" outline onClick={reiniciar} disabled={!historial.length && !rechazos.length}>
          Empezar de nuevo
        </Button>
        <Button color="success" onClick={pasarEntrada} disabled={!prendas}>
          <i className="fas fa-sign-in-alt" /> Pasar a la entrada ({prendas} prenda{prendas === 1 ? "" : "s"})
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default VerificarEntradaQR;
